import { useId } from 'react'
import type { AppVoiceId } from '@tenfour/contracts'
import { SPHERE, projectSpherePoint } from './fieldGeometry'
import { useFieldAnimation } from './useFieldAnimation'
import s from './visuals.module.css'

export type SignupVoice = AppVoiceId

function drawSphere(element: SVGSVGElement, elapsed: number) {
  const points = SPHERE.vertices.map((point) => projectSpherePoint(point, 0.38 + elapsed / 24_000 * Math.PI * 2))
  element.querySelectorAll<SVGLineElement>('[data-edge]').forEach((line, index) => {
    const [leftIndex, rightIndex] = SPHERE.edges[index]
    const left = points[leftIndex]
    const right = points[rightIndex]
    const depth = (left.depth + right.depth) / 2
    line.setAttribute('x1', left.x.toFixed(2))
    line.setAttribute('y1', left.y.toFixed(2))
    line.setAttribute('x2', right.x.toFixed(2))
    line.setAttribute('y2', right.y.toFixed(2))
    line.setAttribute('stroke-width', (0.82 + depth * 1.38).toFixed(2))
    line.setAttribute('stroke-opacity', (0.13 + depth * 0.59).toFixed(3))
  })
  element.querySelectorAll<SVGCircleElement>('[data-node]').forEach((node, index) => {
    const point = points[index]
    node.setAttribute('cx', point.x.toFixed(2))
    node.setAttribute('cy', point.y.toFixed(2))
    node.setAttribute('r', (2.65 + point.depth * 2.35).toFixed(2))
    node.setAttribute('stroke-width', (1.25 + point.depth * 0.8).toFixed(2))
    node.setAttribute('opacity', (0.28 + point.depth * 0.72).toFixed(3))
  })
}

const STILL_POINTS = SPHERE.vertices.map((point) => projectSpherePoint(point, 0.38))

/** The 42-node Signal sphere, with rotation but no traveling line or signal. */
export function SignalSphere({ voice, active = false, className }: {
  voice: SignupVoice
  active?: boolean
  className?: string
}) {
  const gradientId = `sphere-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
  const ref = useFieldAnimation(drawSphere)
  return (
    <svg ref={ref} viewBox="0 0 600 600" className={`${s.sphere} ${className ?? ''}`.trim()}
      data-voice={voice} data-active={active} aria-hidden="true" focusable="false">
      <defs>
        <radialGradient id={gradientId}>
          <stop offset="0" stopColor="currentColor" stopOpacity=".27" />
          <stop offset=".38" stopColor="currentColor" stopOpacity=".12" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0" />
        </radialGradient>
      </defs>
      <circle className={s.sphereCore} cx="300" cy="300" r="156" fill={`url(#${gradientId})`} />
      <g className={s.sphereEdges}>
        {SPHERE.edges.map(([left, right], index) => (
          <line key={index} data-edge={index}
            x1={STILL_POINTS[left].x} y1={STILL_POINTS[left].y}
            x2={STILL_POINTS[right].x} y2={STILL_POINTS[right].y} />
        ))}
      </g>
      <g className={s.sphereNodes}>
        {STILL_POINTS.map((point, index) => (
          <circle key={index} data-node={index} cx={point.x} cy={point.y} r={2.65 + point.depth * 2.35} />
        ))}
      </g>
      <circle className={s.sphereShell} cx="300" cy="300" r="230.5" />
    </svg>
  )
}
